import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ListRowsSkeleton } from './PageSkeletons'
import { createPortal } from 'react-dom'
import { AlertTriangle, CheckCircle2, Loader2, Mail, Pencil, Plus, Trash2, Wand2, X } from 'lucide-react'
import Toggle from './Toggle'
import { autofillRulesApi } from '../services/api'
import { READ_ONLY_MESSAGE } from '../utils/readerAccess'

const EMPTY_FORM = { domain: '', field_name: 'company_id', value: '', label: '', enabled: true }
const errorText = (err, fallback) => err?.response?.data?.error || fallback

function TemplatePicker({ templates, selectedIds, onToggle }) {
  const sorted = [...templates].sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1))
  if (sorted.length === 0) {
    return <p className="text-xs text-gray-400 italic">No templates in this organisation yet.</p>
  }
  return (
    <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
      {sorted.map((t) => (
        <label key={t.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
          <input type="checkbox" checked={selectedIds.includes(t.id)} onChange={() => onToggle(t.id)}
            className="rounded border-gray-300 dark:border-gray-600" />
          <span className="text-gray-700 dark:text-gray-300">{t.name}</span>
          {!t.is_active && <span className="text-xs text-gray-400 italic">(archived)</span>}
        </label>
      ))}
    </div>
  )
}

function RuleModal({ rule, fieldNames, templates, onClose, onSaved, toast }) {
  const [form, setForm] = useState(rule ? {
    domain: rule.domain, field_name: rule.field_name, value: rule.value,
    label: rule.label || '', enabled: rule.enabled,
  } : EMPTY_FORM)
  const [scope, setScope] = useState(rule?.scope === 'specific' ? 'specific' : 'all')
  const [templateIds, setTemplateIds] = useState(rule?.template_ids || [])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }))
  const fieldKnown = fieldNames.includes(form.field_name.trim())
  const toggleTemplate = (id) => setTemplateIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))

  const submit = async (event) => {
    event.preventDefault()
    if (scope === 'specific' && templateIds.length === 0) {
      setError('Pick at least one template, or choose "All templates with this field" instead.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const body = { ...form, template_ids: scope === 'specific' ? templateIds : [] }
      const res = rule
        ? await autofillRulesApi.update(rule.id, body)
        : await autofillRulesApi.create(body)
      toast.success(rule ? 'Rule updated' : 'Rule added',
        `${res.data.rule.domain} → ${res.data.rule.field_name} = ${res.data.rule.value}`)
      onSaved()
    } catch (err) {
      setError(errorText(err, 'Could not save the rule. Please try again.'))
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm" onClick={saving ? undefined : onClose} />
      <form onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="autofill-rule-title" className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h3 id="autofill-rule-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">{rule ? 'Edit rule' : 'Add auto-fill rule'}</h3>
          <button type="button" onClick={onClose} disabled={saving} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="form-label" htmlFor="autofill-domain">E-mail domain</label>
            <div className="flex items-center mt-1.5">
              <span className="inline-flex items-center px-3 py-2.5 rounded-l-lg border border-r-0 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-sm select-none">@</span>
              <input id="autofill-domain" className="form-input !rounded-l-none" value={form.domain} onChange={set('domain')}
                placeholder="example.com" autoCapitalize="none" autoCorrect="off" autoFocus required />
            </div>
            <p className="text-xs text-gray-400 mt-1.5">Orders received at this domain or its subdomains, e.g. orders@example.com.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="form-label" htmlFor="autofill-field">Field to fill</label>
              <input id="autofill-field" className="form-input mt-1.5 font-mono text-sm" value={form.field_name} onChange={set('field_name')}
                list="autofill-field-names" autoCapitalize="none" autoCorrect="off" required />
              <datalist id="autofill-field-names">
                {fieldNames.map((name) => <option key={name} value={name} />)}
              </datalist>
            </div>
            <div>
              <label className="form-label" htmlFor="autofill-value">Value</label>
              <input id="autofill-value" className="form-input mt-1.5" value={form.value} onChange={set('value')}
                placeholder="NL01" maxLength={255} required />
            </div>
          </div>
          {form.field_name.trim() && !fieldKnown && scope === 'all' && (
            <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              No template has a field called "{form.field_name.trim()}", so this rule will not fill anything until one does.
            </p>
          )}
          <div>
            <label className="form-label">Applies to</label>
            <div className="mt-1.5 space-y-2">
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="radio" name="autofill-scope" className="mt-0.5" checked={scope === 'all'}
                  onChange={() => setScope('all')} />
                <span>
                  <span className="text-gray-700 dark:text-gray-300">All templates with this field</span>
                  <span className="block text-xs text-gray-400">Fills "{form.field_name.trim() || 'this field'}" on any template that has it.</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="radio" name="autofill-scope" className="mt-0.5" checked={scope === 'specific'}
                  onChange={() => setScope('specific')} />
                <span>
                  <span className="text-gray-700 dark:text-gray-300">Only these templates</span>
                  <span className="block text-xs text-gray-400">Use this when several templates share the same field name but shouldn't share the same rule (e.g. one template per OpCo).</span>
                </span>
              </label>
            </div>
            {scope === 'specific' && (
              <div className="mt-2">
                <TemplatePicker templates={templates} selectedIds={templateIds} onToggle={toggleTemplate} />
              </div>
            )}
          </div>
          <div>
            <label className="form-label" htmlFor="autofill-label">Note <span className="text-gray-400 font-normal">(optional)</span></label>
            <input id="autofill-label" className="form-input mt-1.5" value={form.label} onChange={set('label')}
              placeholder="Netherlands office" maxLength={255} />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-gray-100 dark:border-gray-800 px-3 py-2.5">
            <span className="text-sm text-gray-700 dark:text-gray-300">Enabled</span>
            <Toggle checked={form.enabled} onChange={() => setForm((f) => ({ ...f, enabled: !f.enabled }))} />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
          <button type="button" onClick={onClose} disabled={saving} className="btn btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} className="btn btn-primary flex items-center gap-2 disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {rule ? 'Save changes' : 'Add rule'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  )
}

function DeleteModal({ rule, onClose, onDeleted, toast }) {
  const [deleting, setDeleting] = useState(false)
  const confirm = async () => {
    setDeleting(true)
    try {
      await autofillRulesApi.remove(rule.id)
      toast.success('Rule deleted', `${rule.domain} will no longer fill ${rule.field_name}.`)
      onDeleted()
    } catch (err) {
      toast.error('Delete failed', errorText(err, 'Please try again.'))
      setDeleting(false)
    }
  }
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm" onClick={deleting ? undefined : onClose} />
      <div role="dialog" aria-modal="true" aria-labelledby="autofill-delete-title" className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md p-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
            <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 id="autofill-delete-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">Delete this rule?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              New orders received at <span className="font-medium">{rule.domain}</span> will no longer get{' '}
              <span className="font-mono">{rule.field_name}</span> = <span className="font-medium">{rule.value}</span>.
              Orders already processed are not changed. To pause it instead, turn it off.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} disabled={deleting} className="btn btn-secondary">Cancel</button>
          <button onClick={confirm} disabled={deleting} className="btn bg-red-600 hover:bg-red-700 text-white flex items-center gap-2 disabled:opacity-50">
            {deleting && <Loader2 className="h-4 w-4 animate-spin" />} Delete
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function TestAddress({ templates, toast }) {
  const [address, setAddress] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [result, setResult] = useState(null)
  const [testing, setTesting] = useState(false)
  const activeTemplates = templates.filter((t) => t.is_active)
  const run = async (event) => {
    event.preventDefault()
    setTesting(true)
    try {
      const res = await autofillRulesApi.testAddress(address, templateId || null)
      setResult(res.data)
    } catch (err) {
      setResult(null)
      toast.error('Test failed', errorText(err, 'Please try again.'))
    } finally {
      setTesting(false)
    }
  }
  return (
    <section className="card card-body space-y-3">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Test an address</h3>
      <form onSubmit={run} className="space-y-2">
        <div className="flex gap-2">
          <input className="form-input text-sm" value={address} onChange={(e) => { setAddress(e.target.value); setResult(null) }}
            placeholder="orders@example.com" autoCapitalize="none" autoCorrect="off" />
          <button type="submit" disabled={testing || !address.trim()} className="btn btn-secondary flex items-center gap-1.5 disabled:opacity-50">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Test
          </button>
        </div>
        {activeTemplates.length > 0 && (
          <select className="form-input text-xs" value={templateId} onChange={(e) => { setTemplateId(e.target.value); setResult(null) }}>
            <option value="">Any template (ignore scoping)</option>
            {activeTemplates.map((t) => <option key={t.id} value={t.id}>For template: {t.name}</option>)}
          </select>
        )}
      </form>
      {result && (result.matches.length ? (
        <div className="rounded-lg border border-green-200 dark:border-green-800/50 bg-green-50 dark:bg-green-900/15 p-3 space-y-1">
          {result.matches.map((m) => (
            <p key={m.field_name} className="flex items-center gap-1.5 text-xs text-green-800 dark:text-green-200">
              <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
              Fills <span className="font-mono">{m.field_name}</span> = <span className="font-semibold">{m.value}</span>
              <span className="text-green-700/70 dark:text-green-300/70">(rule {m.domain})</span>
            </p>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          No enabled rule matches this address{templateId ? ' for that template' : ''}, so nothing would be filled automatically.
        </p>
      ))}
    </section>
  )
}

export default function AutofillRulesSettings({ isReader, toast }) {
  const [rules, setRules] = useState([])
  const [fieldNames, setFieldNames] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [togglingId, setTogglingId] = useState(null)
  // The toast object changes identity whenever a toast shows; depending on it
  // would re-run the load (and re-toast a load error) in a loop.
  const toastRef = useRef(toast)
  toastRef.current = toast

  const load = useCallback(async () => {
    try {
      const res = await autofillRulesApi.list()
      setRules(res.data.rules || [])
      setFieldNames(res.data.field_names || [])
      setTemplates(res.data.templates || [])
    } catch (err) {
      toastRef.current.error('Could not load auto-fill rules', errorText(err, 'Please try again.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const guard = (fn) => () => (isReader ? toast.error(READ_ONLY_MESSAGE) : fn())

  const toggle = async (rule) => {
    setTogglingId(rule.id)
    try {
      const res = await autofillRulesApi.update(rule.id, { enabled: !rule.enabled })
      setRules((rs) => rs.map((r) => (r.id === rule.id ? res.data.rule : r)))
      toast.success(res.data.rule.enabled ? 'Rule turned on' : 'Rule turned off', `${rule.domain} → ${rule.field_name}`)
    } catch (err) {
      toast.error('Update failed', errorText(err, 'Please try again.'))
    } finally {
      setTogglingId(null)
    }
  }

  const closeAndReload = () => { setEditing(null); setDeleting(null); load() }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
      <section className="card card-body space-y-5 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
              <Wand2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Auto-fill by e-mail domain</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                When an order is received at one of these domains, fill a field automatically, for example the company code NL01 for orders sent to example.com.
              </p>
            </div>
          </div>
          {rules.length > 0 && (
            <button onClick={guard(() => setEditing('new'))} title={isReader ? READ_ONLY_MESSAGE : undefined}
              className="btn btn-primary flex items-center gap-2 self-start whitespace-nowrap disabled:opacity-50" disabled={isReader}>
              <Plus className="h-4 w-4" /> Add rule
            </button>
          )}
        </div>

        {loading ? (
          <ListRowsSkeleton rows={3} />
        ) : rules.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No auto-fill rules yet</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-4">Add one for each domain your orders arrive at.</p>
            <button onClick={guard(() => setEditing('new'))} disabled={isReader} title={isReader ? READ_ONLY_MESSAGE : undefined}
              className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-50">
              <Plus className="h-4 w-4" /> Add rule
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
                  {['Domain', 'Field', 'Value', 'Applies to', 'On', ''].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {rules.map((rule) => (
                  <tr key={rule.id} className={rule.enabled ? '' : 'opacity-60'}>
                    <td className="px-3 py-3">
                      <div className="font-medium text-gray-900 dark:text-gray-100">@{rule.domain}</div>
                      {rule.label && <div className="text-xs text-gray-500 dark:text-gray-400">{rule.label}</div>}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-gray-700 dark:text-gray-300">{rule.field_name}</td>
                    <td className="px-3 py-3 font-semibold text-gray-900 dark:text-gray-100">{rule.value}</td>
                    <td className="px-3 py-3 text-xs">
                      {rule.template_names.length ? (
                        <span className="text-gray-600 dark:text-gray-400" title={rule.template_names.join(', ')}>
                          {rule.scope === 'specific' && (
                            <span className="inline-block mr-1 px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[10px] font-semibold uppercase tracking-wide">Only</span>
                          )}
                          {rule.template_names.length === 1 ? rule.template_names[0] : `${rule.template_names.length} templates`}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300"
                          title={rule.scope === 'specific' ? 'The selected template(s) no longer exist' : `No template has a field called ${rule.field_name}`}>
                          <AlertTriangle className="h-3.5 w-3.5" /> {rule.scope === 'specific' ? 'No valid template' : 'No template'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <Toggle checked={rule.enabled} disabled={isReader || togglingId === rule.id}
                        onChange={() => toggle(rule)} title={isReader ? READ_ONLY_MESSAGE : undefined} />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex justify-end gap-1">
                        <button onClick={guard(() => setEditing(rule))} disabled={isReader} title={isReader ? READ_ONLY_MESSAGE : 'Edit'}
                          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40" aria-label="Edit rule">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={guard(() => setDeleting(rule))} disabled={isReader} title={isReader ? READ_ONLY_MESSAGE : 'Delete'}
                          className="p-1.5 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 disabled:opacity-40" aria-label="Delete rule">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="space-y-6">
        {!isReader && <TestAddress templates={templates} toast={toast} />}
        <section className="card card-body space-y-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">How it works</h3>
          <ul className="space-y-2 text-xs text-gray-600 dark:text-gray-400 list-disc pl-4">
            <li>Checked in order: the shared mailbox, the To and Cc addresses, the person who submitted the order, then the sender.</li>
            <li>Only fills a field the template actually has. Reviewers see it marked <span className="font-semibold">Auto-filled</span>.</li>
            <li>A value typed in the Outlook add-in always wins. A value read from the document is replaced, and kept for comparison.</li>
            <li>To put the sender's address itself in a field (e.g. for Boomi), open the template and choose <span className="font-semibold">Add field → E-mail details</span>. Rules don't apply to those fields.</li>
            <li>If two rules give different values for the same order, nothing is filled rather than guessing.</li>
            <li>By default a rule applies to every template with the field. If several templates share a field name but shouldn't share a rule (e.g. one template per OpCo), scope the rule to specific templates.</li>
          </ul>
          <p className="text-xs text-gray-400 border-t border-gray-100 dark:border-gray-800 pt-3">
            Changes apply to orders processed from now on.
          </p>
        </section>
      </div>

      {editing && (
        <RuleModal rule={editing === 'new' ? null : editing} fieldNames={fieldNames} templates={templates}
          onClose={() => setEditing(null)} onSaved={closeAndReload} toast={toast} />
      )}
      {deleting && (
        <DeleteModal rule={deleting} onClose={() => setDeleting(null)} onDeleted={closeAndReload} toast={toast} />
      )}
    </div>
  )
}
